import React, { useState } from 'react';
import {
    Alert,
    AlertIcon,
    Box,
    Button,
    HStack,
    Modal,
    ModalBody,
    ModalCloseButton,
    ModalContent,
    ModalFooter,
    ModalHeader,
    ModalOverlay,
    Text,
    useDisclosure,
    useToast,
    VStack
} from '@chakra-ui/react';
import { sportLabel } from '../../config/marketControlCatalog';
import { TradingClosure, tradingClosureService } from '../../services/tradingClosureService';
import { formatDateTimeInTz } from '../../utils/timezone';

type PendingRecover = { sportId: number; label: string };

const scopeLabel = (closure: TradingClosure): string => {
    switch (closure.scope) {
        case 'GLOBAL': return 'All sports';
        case 'SPORT': return sportLabel(closure.scopeId);
        default: return `Competition ${closure.scopeId}`;
    }
};

interface TradingClosuresBannerProps {
    closures: TradingClosure[];
    loadFailed: boolean;
    timeZone: string;
    onChanged: () => void;
}

export const TradingClosuresBanner: React.FC<TradingClosuresBannerProps> = ({ closures, loadFailed, timeZone, onChanged }) => {
    const [pending, setPending] = useState<PendingRecover | null>(null);
    const [submitting, setSubmitting] = useState(false);
    const { isOpen, onOpen, onClose } = useDisclosure();
    const toast = useToast();

    if (closures.length === 0 && !loadFailed) {
        return null;
    }

    const askRecover = (recover: PendingRecover) => {
        setPending(recover);
        onOpen();
    };

    const recover = async () => {
        if (!pending) return;
        setSubmitting(true);
        try {
            await tradingClosureService.recover([pending.sportId]);
            toast({ title: `Trading recovered for ${pending.label}`, status: 'success', duration: 3000, isClosable: true });
            onChanged();
            onClose();
        } catch (error) {
            toast({ title: 'Failed to recover trading', status: 'error', duration: 3000, isClosable: true });
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <>
            {loadFailed && (
                <Alert status="warning" borderRadius="md">
                    <AlertIcon />
                    Cannot load trading closures: what is closed right now is unknown.
                </Alert>
            )}
            {closures.length > 0 && (
                <Alert status="error" alignItems="flex-start" borderRadius="md">
                    <AlertIcon />
                    <VStack align="stretch" spacing={1} flex={1}>
                        {closures.map(closure => (
                            <HStack key={`${closure.scope}-${closure.scopeId}`} justify="space-between">
                                <Box>
                                    <Text as="span" fontWeight="bold">Trading closed: {scopeLabel(closure)}</Text>
                                    <Text as="span" color="gray.600">
                                        {' '}by {closure.source || 'unknown'}, {formatDateTimeInTz(closure.closedAt, timeZone)}
                                        {closure.reason ? ` (${closure.reason})` : ''}
                                    </Text>
                                </Box>
                                {closure.scope === 'SPORT' && closure.scopeId != null && (
                                    <Button size="xs" colorScheme="blue" variant="outline"
                                            onClick={() => askRecover({ sportId: closure.scopeId as number, label: scopeLabel(closure) })}>
                                        Recover
                                    </Button>
                                )}
                            </HStack>
                        ))}
                    </VStack>
                </Alert>
            )}

            <Modal isOpen={isOpen} onClose={submitting ? () => {} : onClose} isCentered>
                <ModalOverlay />
                <ModalContent>
                    <ModalHeader>Recover trading?</ModalHeader>
                    <ModalCloseButton isDisabled={submitting} />
                    <ModalBody>
                        <Text>Open markets of {pending?.label} will be offered again.</Text>
                    </ModalBody>
                    <ModalFooter gap={3}>
                        <Button variant="ghost" onClick={onClose} isDisabled={submitting}>Cancel</Button>
                        <Button colorScheme="blue" onClick={recover} isLoading={submitting}>Recover</Button>
                    </ModalFooter>
                </ModalContent>
            </Modal>
        </>
    );
};
